import { useEffect, useCallback, useState, useRef } from 'react';
import { driver } from 'driver.js';
import type { Driver, Config } from 'driver.js';
import type { NavigateFunction } from 'react-router-dom';
import 'driver.js/dist/driver.css';
import { getDashboardTourSteps } from '@/components/onboarding/tourSteps';

export const useTour = (isCommunityMember: boolean) => {
  const [driverObj, setDriverObj] = useState<Driver | null>(null);
  const [isTourActive, setIsTourActive] = useState(false);
  const driverRef = useRef<Driver | null>(null);

  useEffect(() => {
    // Mobile check (< 1024px = lg breakpoint in Tailwind)
    const isMobile = window.innerWidth < 1024;

    const moveNext = () => { driverRef.current?.moveNext(); };

    // Driver.js Konfiguration mit dynamischen Steps basierend auf Bildschirmgröße
    const driverConfig: Config = {
      showProgress: true,
      steps: getDashboardTourSteps(isMobile, isCommunityMember, moveNext),

      // Dark Mode Support - wird über CSS gesteuert
      popoverClass: 'driver-popover-custom',

      // Mobile-friendly Settings
      animate: true,
      smoothScroll: true,

      // Verhindert Klicks auf markierte Elemente während der Tour
      disableActiveInteraction: true,

      // Overlay-Klick deaktivieren (Tour bleibt offen)
      overlayClickBehavior: () => {
        // Nichts tun - verhindert Schließen bei Overlay-Klick
      },

      // Custom Button Labels (Deutsch)
      nextBtnText: 'Weiter',
      prevBtnText: 'Zurück',
      doneBtnText: 'Fertig',
      progressText: '{{current}} von {{total}}',

      // Callbacks
      onDestroyed: () => {
        setIsTourActive(false);
      },

      onHighlightStarted: () => {
        setIsTourActive(true);
      }
    };

    const driverInstance = driver(driverConfig);
    driverRef.current = driverInstance;
    setDriverObj(driverInstance);

    return () => {
      if (driverInstance !== null) {
        driverInstance.destroy();
      }
      driverRef.current = null;
    };
  }, [isCommunityMember]);

  const startTour = useCallback((navigate?: NavigateFunction) => {
    if (driverObj !== null) {
      if (navigate) {
        // Mobile check (< 1024px = lg breakpoint in Tailwind)
        const isMobile = window.innerWidth < 1024;

        // Auf Mobil: Sidebar einklappen
        if (isMobile) {
          localStorage.setItem('sidebarCollapsed', 'true');
        }

        // IMMER zum Dashboard navigieren
        navigate('/dashboard');

        // Kurz warten für Navigation und State-Update, dann Tour starten
        setTimeout(() => {
          driverObj.drive();
        }, 150);
      } else {
        // Fallback wenn kein navigate verfügbar
        driverObj.drive();
      }
    }
  }, [driverObj]);

  return {
    startTour,
    isTourActive,
    driverObj
  };
};
